"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { IconAdjustments, IconLoader2 } from "@tabler/icons-react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import {
  type FieldValues,
  type UseFormReturn,
  useForm,
  useWatch,
} from "react-hook-form";
import { z } from "zod";
import { BottomSheetDrawer } from "@/components/bottom-sheet-drawer";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { SheetClose } from "@/components/ui/sheet";
import { useCateringsResults } from "@/features/caterings/api/hooks/use-get-caterings";
import type { CateringCatalog } from "@/features/caterings/types/catering-catalog.type";
import type { CateringFiltersType } from "@/features/caterings/types/catering-filtes.type";
import { getDietPriceRangePresets } from "@/features/diets/constants/diet-price-range-presets";
import { sortingData } from "@/features/diets/data/sorting";
import { serializeTagsClient } from "@/features/diets/helpers/serialize-tags";
import { FilterBlock } from "@/features/filters/components/filter-block";
import { FilterMulti } from "@/features/filters/components/filter-multi/filter-multi";
import { FilterRangeForm } from "@/features/filters/components/filter-range/filter-range-form";
import { FilterTags } from "@/features/filters/components/filter-tags";
import type { FiltersSetup } from "@/features/filters/types/filters-setup.type";
import { defaultLocale } from "@/i18n/defaults";
import { serializeFormValues } from "@/lib/helpers/parsers";
import { objectToQueryParams } from "@/lib/utilities";
import type { ApiPlatformResponseType } from "@/types/api-platform.type";

export const CateringsMobileFiltersForm = ({
  filtersSetup,
  parsedParams,
}: {
  filtersSetup: FiltersSetup;
  parsedParams: CateringFiltersType;
}) => {
  const onCzechLocale = defaultLocale === "cz";
  const t = useTranslations();
  const serializedTags = serializeTagsClient(filtersSetup?.tags);
  const priceRangePresets = getDietPriceRangePresets({
    labels: {
      below30: t("diets.filters.price.presets.below30"),
      between30And60: t("diets.filters.price.presets.between30And60"),
      between60And80: t("diets.filters.price.presets.between60And80"),
      above80: t("diets.filters.price.presets.above80"),
    },
    maxValue: filtersSetup.prices.defaultTo,
    locale: defaultLocale,
  });
  const defaultCaloricity =
    filtersSetup.calories.find(
      (calorie) => calorie.label === parsedParams.caloricity?.toString(),
    )?.label || "";

  const form = useForm<FieldValues>({
    resolver: zodResolver(
      z.object({
        sortOption: z.string().optional(),
        tagsSlugs: z.array(z.string()).optional(),
        caloricity: z.string().optional(),
        "price[gte]": z.string().optional(),
        "price[lte]": z.string().optional(),
        numberOfMeals: z.string().optional(),
      }),
    ),
    defaultValues: {
      sortOption: parsedParams.sortOption || "default",
      tagsSlugs: parsedParams.tagsSlugs?.split(",") || [],
      caloricity: defaultCaloricity,
      "price[gte]": parsedParams["price[gte]"] || "",
      "price[lte]": parsedParams["price[lte]"] || "",
      numberOfMeals: parsedParams.numberOfMeals || "",
    },
  });

  useEffect(() => {
    form.reset({
      sortOption: parsedParams.sortOption || "default",
      tagsSlugs: parsedParams.tagsSlugs?.split(",") || [],
      caloricity: defaultCaloricity,
      "price[gte]": parsedParams["price[gte]"] || "",
      "price[lte]": parsedParams["price[lte]"] || "",
      numberOfMeals: parsedParams.numberOfMeals || "",
    });
  }, [
    form,
    defaultCaloricity,
    parsedParams.numberOfMeals,
    parsedParams.sortOption,
    parsedParams.tagsSlugs,
    parsedParams["price[gte]"],
    parsedParams["price[lte]"],
  ]);

  const tagsSlugs = useWatch({
    control: form.control,
    name: "tagsSlugs",
  });
  const caloricity = useWatch({
    control: form.control,
    name: "caloricity",
  }) as CateringFiltersType["caloricity"];

  const priceGte = useWatch({
    control: form.control,
    name: "price[gte]",
  });
  const priceLte = useWatch({
    control: form.control,
    name: "price[lte]",
  });
  const numberOfMeals = useWatch({
    control: form.control,
    name: "numberOfMeals",
  });

  const {
    data: cateringsResults,
    isPending,
    refetch,
  } = useCateringsResults({
    filters: {
      ...serializeFormValues({
        tagsSlugs,
        numberOfMeals: numberOfMeals?.toString() || "",
      }),
      caloricity: caloricity,
      "price[gte]": priceGte?.toString() || "",
      "price[lte]": priceLte?.toString() || "",
    },
  });

  useEffect(() => {
    if (!form.formState.isDirty) {
      return;
    }

    refetch();
  }, [form.formState.isDirty, refetch]);

  return (
    <Form {...form}>
      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <div className="rounded-lg bg-brand-grey1 p-4">
              {filtersSetup && serializedTags && (
                <FilterTags
                  filtersSetup={filtersSetup}
                  serializedTags={serializedTags}
                  collapsible={false}
                  asForm={true}
                  form={form}
                />
              )}
            </div>
          </div>
        }
        title={t("filters.labels.diet_type")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.diet_type")}{" "}
            {tagsSlugs?.length > 0 ? `(${tagsSlugs?.length})` : ""}
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            results={cateringsResults}
            fieldName="tagsSlugs"
          />
        }
      />

      {!onCzechLocale && (
        <BottomSheetDrawer
          className="z-99999999"
          content={
            <div className="p-4">
              <div className="rounded-lg bg-brand-grey1 p-4">
                <FilterMulti
                  filter={{
                    label: t("filters.labels.caloricity"),
                    name: "caloricity",
                    total: filtersSetup.calories.length,
                    value: [],
                    options: filtersSetup.calories.map((item) => ({
                      label: t("filters.kcal", { kcal: item.label }),
                      value: item.label,
                    })),
                  }}
                  paramName="caloricity"
                  type="button"
                  multiple={false}
                  stretch={false}
                  asForm={true}
                  form={form}
                />
              </div>
            </div>
          }
          title={t("filters.labels.caloricity")}
          trigger={
            <Button variant="outline">
              {t("filters.labels.caloricity")} {caloricity ? `(1)` : ""}
            </Button>
          }
          footer={
            <Footer
              form={form}
              isPending={isPending}
              results={cateringsResults}
              fieldName="caloricity"
            />
          }
        />
      )}

      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <div className="rounded-lg bg-brand-grey1 p-4">
              <FilterMulti
                filter={{
                  label: t("filters.labels.numberOfMeals"),
                  name: "numberOfMeals",
                  total: filtersSetup.meals.length,
                  value: [],
                  options: filtersSetup.meals.map((item) => ({
                    label: item.name,
                    value: item.name,
                  })),
                }}
                paramName="numberOfMeals"
                type="button"
                multiple={false}
                stretch={true}
                asForm={true}
                form={form}
              />
            </div>
          </div>
        }
        title={t("filters.labels.meals")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.meals")}{" "}
            {form.getValues("numberOfMeals")?.length > 0
              ? `(${form.getValues("numberOfMeals")?.length})`
              : ""}
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            results={cateringsResults}
            fieldName="price"
          />
        }
      />

      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <div className="rounded-lg bg-brand-grey1 p-4">
              <FilterRangeForm
                form={form}
                labelEnd={t("diets.filters.price.to")}
                labelStart={t("diets.filters.price.from")}
                maxValue={filtersSetup.prices.defaultTo}
                minValue={filtersSetup.prices.defaultFrom}
                paramEnd="price[lte]"
                paramStart="price[gte]"
                rangePresets={priceRangePresets}
                unit={t(`common.currencies.${defaultLocale}`)}
              />
            </div>
          </div>
        }
        title={t("filters.labels.price")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.price")} {priceGte || priceLte ? `(1)` : ""}
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            results={cateringsResults}
            fieldName={["price[gte]", "price[lte]"]}
          />
        }
      />

      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <Accordion
              className="flex w-full flex-col gap-4"
              defaultValue={["tagsSlugs"]}
              type="multiple"
            >
              <FilterBlock label={t("filters.sort")} name="sortOption">
                <FilterMulti
                  layout="horizontalScroll"
                  collapsible={false}
                  multiple={false}
                  asForm={true}
                  form={form}
                  filter={{
                    label: "",
                    name: "sortOption",
                    total: sortingData.length,
                    value: [],
                    options: sortingData.map((item) => ({
                      label: (
                        <span className="flex items-center gap-2">
                          {t(`diets.filters.sort.${item.label}`)}
                        </span>
                      ),
                      value: item.value,
                    })),
                  }}
                  paramName="sortOption"
                  type="button"
                />
              </FilterBlock>
              {serializedTags && (
                <FilterBlock
                  filterCounter
                  label={t("filters.labels.diet_type")}
                  name="tagsSlugs"
                >
                  <FilterTags
                    filtersSetup={filtersSetup}
                    serializedTags={serializedTags}
                    asForm={true}
                    form={form}
                  />
                </FilterBlock>
              )}

              <FilterBlock
                filterCounter
                label={t("filters.labels.caloricity")}
                name="caloricity"
              >
                <FilterMulti
                  filter={{
                    label: t("filters.labels.caloricity"),
                    name: "caloricity",
                    total: filtersSetup.calories.length,
                    value: [],
                    options: filtersSetup.calories.map((item) => ({
                      label: t("filters.kcal", { kcal: item.label }),
                      value: item.label,
                    })),
                  }}
                  paramName="caloricity"
                  type="button"
                  multiple={false}
                  stretch={false}
                  asForm={true}
                  form={form}
                />
              </FilterBlock>

              <FilterBlock
                filterCounter="mobile"
                label={t("filters.labels.meals")}
                name="numberOfMeals"
              >
                <FilterMulti
                  filter={{
                    label: t("filters.labels.numberOfMeals"),
                    name: "numberOfMeals",
                    total: filtersSetup.meals.length,
                    value: [],
                    options: filtersSetup.meals.map((item) => ({
                      label: item.name,
                      value: item.name,
                    })),
                  }}
                  paramName="numberOfMeals"
                  type="button"
                  multiple={false}
                  stretch={true}
                  asForm={true}
                  form={form}
                />
              </FilterBlock>
              <FilterBlock
                filterCounter
                label={t("diets.filters.price.label")}
                name="price[gte]"
              >
                <FilterRangeForm
                  form={form}
                  labelEnd={t("diets.filters.price.to")}
                  labelStart={t("diets.filters.price.from")}
                  maxValue={filtersSetup.prices.defaultTo}
                  minValue={filtersSetup.prices.defaultFrom}
                  paramEnd="price[lte]"
                  paramStart="price[gte]"
                  rangePresets={priceRangePresets}
                  unit={t(`common.currencies.${defaultLocale}`)}
                />
              </FilterBlock>
            </Accordion>
          </div>
        }
        title={t("filters.filter")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.all_filters")}{" "}
            <IconAdjustments className="ml-2" />
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            results={cateringsResults}
            fieldName="all"
          />
        }
      />
    </Form>
  );
};

const Footer = ({
  form,
  isPending,
  results,
  fieldName,
}: {
  form: UseFormReturn<FieldValues>;
  isPending: boolean;
  results: ApiPlatformResponseType<CateringCatalog> | undefined;
  fieldName: string | string[];
}) => {
  const { replace } = useRouter();
  const pathname = usePathname();
  const t = useTranslations("filters");

  const handleReset = () => {
    if (fieldName === "all") {
      form.reset();
    } else if (Array.isArray(fieldName)) {
      for (const name of fieldName) {
        form.setValue(name, "", {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    } else {
      form.resetField(fieldName);
    }
  };

  const handleApply = () => {
    form.handleSubmit(onSubmit)();
  };

  const onSubmit = (data: FieldValues) => {
    const values = serializeFormValues({
      ...data,
      "price[gte]": form.getValues("price[gte]"),
      "price[lte]": form.getValues("price[lte]"),
    });

    replace(
      `${pathname}?${objectToQueryParams(values, new URLSearchParams()).toString()}`,
    );
  };

  // count not empty fields
  const countNotEmptyFields = Object.values(form.getValues()).filter(
    (value) => value !== "" && value !== undefined && value !== null,
  ).length;

  return (
    <div className="flex gap-2">
      <Button variant="outline" className="flex-1" onClick={handleReset}>
        {fieldName === "all"
          ? t("reset_all") +
            (countNotEmptyFields > 0 ? ` (${countNotEmptyFields})` : "")
          : t("reset")}
      </Button>
      <SheetClose asChild>
        <Button
          variant="secondary"
          disabled={isPending && !form.formState.isDirty}
          className="flex-1"
          onClick={handleApply}
        >
          {isPending && form.formState.isDirty ? (
            <IconLoader2 className="ml-2 animate-spin" />
          ) : (
            t("show", { count: results?.["hydra:totalItems"] || 0 })
          )}
        </Button>
      </SheetClose>
    </div>
  );
};
